import { safeAssert as assert } from '@upright/common/safe-assert';
import { Dictionary, get, has, isNil, keys, mapValues, pick, round, sum, values, zip } from 'lodash';
import { assertEqualLength } from './assertion-helpers';

export function zipOrThrow<T, U>(l1: T[], l2: U[]): Array<[T, U]> {
  assertEqualLength(l1, l2);
  const zipped = zip(l1, l2);
  // Type casting is ok because equal length has been asserted
  const zippedTypeCasted: Array<[T, U]> = zipped.map(([a, b]) => [a as T, b as U]);
  return zippedTypeCasted;
}

export function getOrThrow<T extends object, U extends keyof T>(object: T, key: U, error?: string): NonNullable<T[U]> {
  if (!has(object, key)) {
    const errorText = error || `key ${key as string} not found in object`;
    throw Error(errorText);
  }
  return object[key] as NonNullable<T[U]>;
}

/** Reusable weighted mean helper. See unit test for detailed behaviour */
export function weightedMean<T extends Dictionary<number>>(
  valuesByKey: T,
  weightsByKey: T,
  defaultValue: number | undefined = undefined,
  weightsAreNormalized = false,
): number {
  const imputedValues = imputeWith(valuesByKey, keys(weightsByKey), defaultValue);
  const curatedWeights = mapValues(imputedValues, (_value, key) => {
    const weight = get(weightsByKey, key, undefined);
    if (isNil(weight)) {
      return 0;
    }
    if (!isFinite(weight) || weight < 0) {
      throw Error(`Invalid weight ${weight} for key ${key}`);
    }
    return weight;
  });
  const weightSum = sum(values(curatedWeights));
  if (!(weightSum > 0) || !isFinite(weightSum)) {
    throw Error(`Weights sum ${weightSum} is not a finite positive number`);
  }

  const weightedValues = mapValues(imputedValues, (value, key) => {
    const weight = get(curatedWeights, key, undefined);
    assert(!isNil(weight));
    assert(isFinite(value), `non-finite value ${value} for key ${key}`);
    return value * weight;
  });
  const weightedSum = sum(values(weightedValues));
  return weightsAreNormalized ? weightedSum : weightedSum / weightSum;
}

export function imputeWith<U>(
  valuesByKey: { [key: string]: U },
  keysParam: string[],
  defaultValue: U | undefined | null,
) {
  if (isNil(defaultValue)) return valuesByKey;

  const valuesWithUndefinedsReplacedByDefault = keysParam.reduce((acc: { [key: string]: U }, key) => {
    const value = valuesByKey[key] as U | undefined;
    if (isNil(value)) {
      acc[key] = defaultValue;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

  return valuesWithUndefinedsReplacedByDefault;
}

export function snapToInt(value: number, precision = 4): number {
  const intValue = round(value, 0);
  const roundedValue = round(value, precision);
  return intValue === roundedValue ? intValue : value;
}

/**
 * Picks only the provided attributes from an object, if the object exists. If you use an
 * actual readonly array of strings as the "attributes" prop instead of Array<keyof T>, the return type will
 * nicely tell you which attributes have been picked.
 */
export function pickAttributesIfExists<T extends object, K extends keyof T>(
  object: T | undefined,
  attributes: readonly K[],
) {
  return object ? pick(object, attributes) : undefined;
}

/**
 The same as above except does not allow an undefined object to be given.
 */
export function pickAttributes<T extends object, K extends keyof T>(object: T, attributes: readonly K[]) {
  return pick(object, attributes);
}
